import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { SEOHead } from '../components/common/SEOHead';
import { Breadcrumbs } from '../components/common/Breadcrumbs';
import { getBlogs } from '../lib/api';
import { formatDate, truncate } from '../lib/utils';
import { ArrowRight } from 'lucide-react';

export default function BlogPage() {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getBlogs()
      .then(res => setBlogs(res.data))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <SEOHead
        title="Blog - Fragrance Industry Insights & News"
        description="Stay updated with the latest trends, news, and insights from the fragrance industry. Tips for retailers and wholesalers."
        canonical="https://passionofaroma.com/blog"
      />

      {/* Hero */}
      <section className="pt-32 pb-16 bg-[#0B1F3B]" data-testid="blog-hero">
        <div className="container-custom">
          <Breadcrumbs items={[{ label: 'Blog' }]} />
          <div className="mt-6 max-w-2xl">
            <span className="label-text mb-4 block">Insights</span>
            <h1 className="heading-xl text-white mb-4">Our Blog</h1>
            <p className="text-gray-300 text-lg">
              Industry insights, fragrance trends, and tips for growing your 
              business in the world of premium fragrances.
            </p>
          </div>
        </div>
      </section>

      {/* Blog Posts */}
      <section className="py-16 bg-[#F7F6F3]" data-testid="blog-content">
        <div className="container-custom">
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="skeleton h-96" />
              ))}
            </div>
          ) : blogs.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {blogs.map((blog) => (
                <article
                  key={blog.id}
                  className="bg-white group"
                  data-testid={`blog-card-${blog.slug}`}
                >
                  {blog.image && (
                    <Link to={`/blog/${blog.slug}`} className="block overflow-hidden">
                      <img
                        src={blog.image}
                        alt={blog.title}
                        className="w-full aspect-video object-cover image-hover"
                      />
                    </Link>
                  )}
                  <div className="p-6">
                    <time className="text-xs text-gray-400 uppercase tracking-widest">
                      {formatDate(blog.created_at)}
                    </time>
                    <h2 className="heading-sm mt-2 mb-3 group-hover:text-[#D4AF37] transition-colors">
                      <Link to={`/blog/${blog.slug}`}>{blog.title}</Link>
                    </h2>
                    <p className="text-gray-600 text-sm mb-4">
                      {truncate(blog.excerpt || blog.content, 120)}
                    </p>
                    <Link
                      to={`/blog/${blog.slug}`}
                      className="inline-flex items-center gap-2 text-[#D4AF37] text-sm uppercase tracking-widest font-medium"
                    >
                      Read More
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-white">
              <h2 className="heading-md mb-4">Coming Soon</h2>
              <p className="text-gray-500">
                We're working on bringing you valuable content. Check back soon!
              </p>
            </div>
          )}
        </div>
      </section>
    </>
  );
}
